/**
 * Onboarding Guide Configuration
 * 
 * Defines tour steps for each page/route in the recruiter panel.
 * Each step targets a CSS selector and provides a tooltip with positioning.
 */

export interface GuideStep {
    /** CSS selector to find the target element */
    selector: string;
    /** Title shown in the tooltip */
    title: string;
    /** Description text for the tooltip */
    description: string;
    /** Preferred position of the tooltip relative to the element */
    position: 'top' | 'bottom' | 'left' | 'right';
    /** Optional: override highlight padding (px) */
    highlightPadding?: number;
}

export interface PageTour {
    /** Identifier for this tour */
    id: string;
    /** Friendly name for the tour */
    name: string;
    /** Path patterns this tour applies to */
    pathPatterns: string[];
    /** Individual steps of the tour */
    steps: GuideStep[];
}

/**
 * All available tours in the application.
 * Each tour is bound to a set of path patterns and contains
 * ordered steps that highlight UI elements with explanatory tooltips.
 */
export const getPageTours = (lang: 'es' | 'en'): PageTour[] => {
    const isEn = lang === 'en';
    return [
        {
            id: 'recruiter-dashboard',
            name: isEn ? 'Vacancies Dashboard' : 'Panel de Vacantes',
            pathPatterns: ['/recruiter', '/recruiter/'],
            steps: [
                {
                    selector: '.recruiter-sidebar-nav',
                    title: isEn ? 'Main Navigation' : 'Navegación Principal',
                    description: isEn
                        ? 'From here you can create vacancies, view the main dashboard, access premium features, and settings.'
                        : 'Desde aquí puedes crear vacantes, ver el dashboard principal, acceder a funciones premium y configurar tu cuenta.',
                    position: 'right',
                },
                {
                    selector: '.vacancies-table, .table-wrapper',
                    title: isEn ? 'Your Vacancies' : 'Tus Vacantes',
                    description: isEn
                        ? 'Here is the table with all your active vacancies and drafts. You can view the title, status, and dates at a glance.'
                        : 'Aquí se muestra la tabla con todas tus vacantes activas y borradores. Puedes ver el título, estado y fechas de un vistazo.',
                    position: 'top',
                    highlightPadding: 16,
                },
                {
                    selector: '.header-filter-input',
                    title: isEn ? 'Search and Filters' : 'Búsqueda y Filtros',
                    description: isEn
                        ? 'Quickly filter by job title or status (Active, Paused, etc.) to organize your work.'
                        : 'Filtra rápidamente por nombre de puesto o por estado (Activas, Pausadas, etc.) para organizar tu trabajo.',
                    position: 'bottom',
                },
                {
                    selector: '.action-buttons',
                    title: isEn ? 'Vacancy Actions' : 'Acciones de Vacante',
                    description: isEn
                        ? 'Each button allows a key action: view candidates, edit AI questions, copy public link, or enter the full editor.'
                        : 'Cada botón te permite una acción clave: ver candidatos, editar preguntas de IA, copiar el enlace público o entrar al editor completo.',
                    position: 'left',
                },
                {
                    selector: '.recruiter-sidebar-nav-item:last-of-type, .nav-item-icon',
                    title: isEn ? 'Settings & Theme' : 'Configuración y Tema',
                    description: isEn
                        ? 'Customize your visual theme (dark, light, violet) and language. Go to Settings to change them.'
                        : 'Personaliza el tema visual (oscuro, claro, violeta) y el idioma de la plataforma desde Configuración.',
                    position: 'right',
                },
                {
                    selector: '.recruiter-lang-selector',
                    title: isEn ? 'Panel Language' : 'Idioma del Panel',
                    description: isEn
                        ? 'Switch between Spanish and English instantly to work in your preferred language.'
                        : 'Cambia entre Español e Inglés instantáneamente para trabajar en el idioma que prefieras.',
                    position: 'bottom',
                },
                {
                    selector: '.notification-bell-container',
                    title: isEn ? 'Notification Center' : 'Centro de Notificaciones',
                    description: isEn
                        ? 'Stay up to date with new applications. The bell will notify you when a candidate applies.'
                        : 'Mantente al tanto de las nuevas aplicaciones. La campanita te avisará cuando un candidato se postule.',
                    position: 'bottom',
                },
            ],
        },
        {
            id: 'vacancy-questions',
            name: isEn ? 'Edit Questions' : 'Edición de Preguntas',
            pathPatterns: ['/recruiter/vacancies/*/questions'],
            steps: [
                {
                    selector: '.questions-list-wrapper',
                    title: isEn ? 'AI Questions' : 'Preguntas de la IA',
                    description: isEn
                        ? 'These are the questions our AI has specifically designed for this vacancy based on the Job Description.'
                        : 'Estas son las preguntas que nuestra IA ha diseñado específicamente para esta vacante basándose en el Job Description.',
                    position: 'bottom',
                },
                {
                    selector: '.btn-edit-question-item',
                    title: isEn ? 'Customize Question' : 'Personalizar Pregunta',
                    description: isEn
                        ? 'You can edit the text of any question to exactly match what you want to evaluate.'
                        : 'Puedes editar el texto de cualquier pregunta para que se ajuste exactamente a lo que deseas evaluar.',
                    position: 'left',
                },
                {
                    selector: '.btn-add-question',
                    title: isEn ? 'Generate More' : 'Generar Más',
                    description: isEn
                        ? 'Need to evaluate something else? Ask the AI to generate additional technical or behavioral questions.'
                        : '¿Necesitas evaluar algo más? Pide a la IA que genere preguntas adicionales de tipo técnico o de comportamiento.',
                    position: 'top',
                },
                {
                    selector: '.btn-approve',
                    title: isEn ? 'Save and Approve' : 'Guardar y Aprobar',
                    description: isEn
                        ? 'Once you are satisfied with the questions, save them so they are available in the candidate flow.'
                        : 'Una vez que estés satisfecho con las preguntas, guárdalas para que estén disponibles en el flujo del candidato.',
                    position: 'top',
                },
            ],
        },
        {
            id: 'vacancy-metrics',
            name: isEn ? 'Metrics Analysis' : 'Análisis de Métricas',
            pathPatterns: ['/recruiter/metrics/*'],
            steps: [
                {
                    selector: '.kpi-row',
                    title: isEn ? 'Executive Summary' : 'Resumen Ejecutivo',
                    description: isEn
                        ? 'View key indicators: total applications, average talent score, and number of viable candidates.'
                        : 'Mira los indicadores clave: total de aplicaciones, score promedio del talento y cantidad de candidatos viables.',
                    position: 'bottom',
                },
                {
                    selector: '.charts-grid > div:nth-child(1)',
                    title: isEn ? 'Score Distribution' : 'Distribución de Scores',
                    description: isEn
                        ? 'Understand how talent is distributed. Select a range to see which specific candidates fall into that category.'
                        : 'Entiende cómo se distribuye el talento. Selecciona un rango para ver qué candidatos específicos caen en esa categoría.',
                    position: 'right',
                },
                {
                    selector: '.charts-grid > div:nth-child(2)',
                    title: isEn ? 'Compatibility Radar' : 'Radar de Compatibilidad',
                    description: isEn
                        ? 'Visualize the talent balance across experience, skills, and education in aggregate.'
                        : 'Visualiza el balance del talento entre experiencia, habilidades y educación de forma agregada.',
                    position: 'left',
                },
                {
                    selector: '.charts-grid > div:nth-child(3)',
                    title: isEn ? 'Source ROI' : 'ROI de Fuentes',
                    description: isEn
                        ? 'Identify which channel brings you the best candidates (highest average score), not just highest volume.'
                        : 'Identifica qué canal te está trayendo a los mejores candidatos (mayor score promedio), no solo mayor volumen.',
                    position: 'top',
                },
            ],
        },
        {
            id: 'recruiter-home',
            name: isEn ? 'Create Vacancy' : 'Crear Vacante',
            pathPatterns: ['/recruiter/new'],
            steps: [
                {
                    selector: '.upload-area',
                    title: isEn ? 'Upload your Job Description' : 'Sube tu Job Description',
                    description: isEn
                        ? 'Drag your PDF or DOCX file. Our AI will automatically extract skills, responsibilities, and job requirements.'
                        : 'Arrastra tu archivo PDF o DOCX. Nuestra IA extraerá automáticamente habilidades, responsabilidades y requisitos del puesto.',
                    position: 'bottom',
                },
            ],
        },
        {
            id: 'vacancy-editor',
            name: isEn ? 'Vacancy Editor' : 'Editor de Vacante',
            pathPatterns: ['/recruiter/vacancies/*/edit'],
            steps: [
                {
                    selector: '.editor-header',
                    title: isEn ? 'Job Details' : 'Detalles del Puesto',
                    description: isEn
                        ? 'Adjust the title and main description. Everything you edit here will be used by the AI to evaluate candidates.'
                        : 'Ajusta el título y la descripción principal. Todo lo que edites aquí será usado por la IA para evaluar a los candidatos.',
                    position: 'bottom',
                },
                {
                    selector: '.skills-section',
                    title: isEn ? 'Skills Management' : 'Gestión de Habilidades',
                    description: isEn
                        ? 'Reorganize technical and soft skills. You can add custom tags that the AI must validate.'
                        : 'Reorganiza las habilidades técnicas y blandas. Puedes agregar etiquetas personalizadas que la IA debe validar.',
                    position: 'bottom',
                },
                {
                    selector: '.editor-save-btn',
                    title: isEn ? 'Update Vacancy' : 'Actualizar Vacante',
                    description: isEn
                        ? 'Save your changes so the AI recalibrates according to your new requirements.'
                        : 'Guarda tus cambios para que la IA se recalibre según tus nuevos requisitos.',
                    position: 'top',
                },
            ],
        },
        {
            id: 'recruiter-workspace',
            name: isEn ? 'Candidate Workspace' : 'Workspace de Candidatos',
            pathPatterns: ['/recruiter/workspace/*'],
            steps: [
                {
                    selector: '.candidates-list',
                    title: isEn ? 'Your Candidates' : 'Tus Candidatos',
                    description: isEn
                        ? 'Explore the list of people who applied. The suitability score helps you prioritize the best profiles.'
                        : 'Explora la lista de personas que aplicaron. El score de idoneidad te ayuda a priorizar a los mejores perfiles.',
                    position: 'right',
                },
                {
                    selector: '.candidate-detail',
                    title: isEn ? 'Deep Analysis' : 'Análisis Profundo',
                    description: isEn
                        ? 'Review the reason for the score: strengths extracted by AI and areas where the candidate might not be a 100% fit.'
                        : 'Revisa el porqué del score: fortalezas extráidas por IA y áreas donde el candidato podría no encajar al 100%.',
                    position: 'left',
                },
            ],
        },
        {
            id: 'recruiter-settings',
            name: isEn ? 'Settings' : 'Configuración',
            pathPatterns: ['/recruiter/settings'],
            steps: [
                {
                    selector: '.recruiter-settings-tabs',
                    title: isEn ? 'Settings Tabs' : 'Pestañas de Configuración',
                    description: isEn
                        ? 'Navigate between profile, notifications, theme, and language settings.'
                        : 'Navega entre configuración de perfil, notificaciones, tema visual e idioma.',
                    position: 'bottom',
                },
                {
                    selector: '.recruiter-settings-theme-circles',
                    title: isEn ? 'Visual Theme' : 'Tema Visual',
                    description: isEn
                        ? 'Choose between 5 visual themes: Default Blue, Violet, Pure Black, Pure White, and Violet Inverted.'
                        : 'Elige entre 5 temas visuales: Azul predeterminado, Violeta, Negro puro, Blanco puro y Violeta invertido.',
                    position: 'bottom',
                },
            ],
        },
    ];
};

/**
 * Find the best matching tour for a given pathname.
 * Uses specificity: more specific patterns are preferred.
 */
export function findTourForPath(pathname: string, lang: 'es' | 'en' = 'es'): PageTour | null {
    const pageTours = getPageTours(lang);

    // 1. Try exact match first
    for (const tour of pageTours) {
        for (const pattern of tour.pathPatterns) {
            if (pattern === pathname || pattern === pathname.replace(/\/$/, '')) {
                return tour;
            }
        }
    }

    // 2. Try wildcard patterns (e.g., /recruiter/vacancies/*/edit)
    for (const tour of pageTours) {
        for (const pattern of tour.pathPatterns) {
            if (pattern.includes('*')) {
                const regex = new RegExp('^' + pattern.replace(/\*/g, '[^/]+') + '$');
                if (regex.test(pathname)) {
                    return tour;
                }
            }
        }
    }

    // No fallback to layout tour - only show if there's a specific tour for this page
    return null;
}
